import { getDefaultKindSelectState } from "../../../components/antd/kindselect/KindSelect";
import { inputToTags, tagsToInput } from "../../../mst/kinds/utils";
import { getDefaultTracingInput, inputToTracing, tracingToInput } from "../../../pages/gvc/utils";
import { defaultValues } from "../../../services/defaults";
import { parseLink } from "../../../utils/linkParser/linkParser";
import { getDefaultBaseInput, NameValuePair, OptimisticConcurrency } from "../base";
import { Org, OrgLogConfig } from "./entity";
import {
  CloudWatchLoggingInput,
  CoralogixLoggingInput,
  DatadogLoggingInput,
  ElasticLoggingAwsProviderInput,
  ElasticLoggingElastiCloudProviderInput,
  ElasticLoggingGenericESProviderInput,
  ElasticLoggingInput,
  FluentdLoggingInput,
  LoggingInput,
  LoggingProvider,
  LogzioLoggingInput,
  OpenTelemetryLoggingInput,
  OrgInput,
  S3LoggingInput,
  StackdriverLoggingInput,
  SyslogLoggingInput,
} from "./input";
import {
  CloudWatchLogging,
  CoralogixLogging,
  DatadogLogging,
  ElasticAwsProvider,
  ElasticLogging,
  ElastiCloudProvider,
  FluentdLogging,
  GenericESProvider,
  LogzioLogging,
  OpenTelemetryLogging,
  S3Logging,
  StackdriverLogging,
  SyslogLogging,
} from "./logging";
import { OrgPatch } from "./patch";
import { v4 as uuidv4 } from "uuid";

export function getDefaultOrgInput(): OrgInput {
  const df = defaultValues.org;
  return {
    ...getDefaultBaseInput(),
    id: "",
    version: 0,
    authConfig: {
      domainAutoMembers: "none",
      samlOnly: false,
      isSaml: false,
      samlDomain: "",
    },
    logging: [],
    observability: {
      defaultAlertEmails: [],
      logsRetentionDays: String(df.observability.logsRetentionDays.default),
      metricsRetentionDays: String(df.observability.metricsRetentionDays.default),
      tracesRetentionDays: String(df.observability.tracesRetentionDays.default),
    },
    security: {
      threatDetection: {
        isEnabled: false,
        minimumSeverity: df.threatDetection.minimumSeverity.default as any,
        syslog: {
          host: "",
          port: "",
          transport: df.threatDetection.syslog.transport.default as any,
        },
      },
    },
    sessionTimeoutSeconds: df.sessionTimeoutSeconds.default,
    tracing: getDefaultTracingInput(),
    meta: {
      activeKeyExternalLog: "",
    },
  };
}

export function getDefaultCloudWatchLoggingInput(): CloudWatchLoggingInput {
  return {
    credentials: getDefaultKindSelectState(),
    extractFields: [],
    groupName: "",
    region: "" as any,
    retentionDays: "",
    streamName: "",
  };
}

export function getDefaultCoralogixLoggingInput(): CoralogixLoggingInput {
  const df = defaultValues.org.logging.coralogix;
  return {
    cluster: df.cluster.default,
    credentials: getDefaultKindSelectState(),
    app: "",
    subsystem: "",
  };
}

export function getDefaultDatadogLoggingInput(): DatadogLoggingInput {
  const df = defaultValues.org.logging.datadog;

  return {
    credentials: getDefaultKindSelectState(),
    host: df.host.default,
  };
}

export function getDefaultElasticLoggingAWSProviderInput(): ElasticLoggingAwsProviderInput {
  return {
    credentials: getDefaultKindSelectState(),
    host: "",
    index: "",
    port: defaultValues.org.logging.elastic.aws.port.default,
    region: "",
    type: "",
  };
}

export function getDefaultElasticLoggingElastiCloudProviderInput(): ElasticLoggingElastiCloudProviderInput {
  return {
    credentials: getDefaultKindSelectState(),
    cloudId: "",
    index: "",
    type: "",
  };
}

export function getDefaultElasticLoggingGenericProviderInput(): ElasticLoggingGenericESProviderInput {
  return {
    credentials: getDefaultKindSelectState(),
    host: "",
    index: "",
    path: "",
    port: defaultValues.org.logging.elastic.generic.port.default,
    type: "",
  };
}

export function getDefaultElasticLoggingInput(): ElasticLoggingInput {
  return {
    provider: "generic",
    aws: getDefaultElasticLoggingAWSProviderInput(),
    elasticCloud: getDefaultElasticLoggingElastiCloudProviderInput(),
    generic: getDefaultElasticLoggingGenericProviderInput(),
  };
}

export function getDefaultFluentdLoggingInput(): FluentdLoggingInput {
  return {
    host: "",
    port: "",
  };
}

export function getDefaultLogzioLoggingInput(): LogzioLoggingInput {
  return {
    credentials: getDefaultKindSelectState(),
    listenerHost: defaultValues.org.logging.logzio.listenerHost.default,
  };
}

export function getDefaultS3LoggingInput(): S3LoggingInput {
  return {
    bucket: "",
    credentials: getDefaultKindSelectState(),
    prefix: "/",
    region: "",
  };
}

export function getDefaultStackdriverLoggingInput(): StackdriverLoggingInput {
  return {
    credentials: getDefaultKindSelectState(),
    location: "" as any,
  };
}

export function getDefaultSyslogLoggingInput(): SyslogLoggingInput {
  const df = defaultValues.org.logging.syslog;

  return {
    format: df.format.default as any,
    host: "",
    mode: df.mode.default as any,
    port: "",
    severity: df.severity.default,
  };
}

export function getDefaultOpenTelemetryLoggingInput(): OpenTelemetryLoggingInput {
  return {
    endpoint: "",
    headers: [],
    credentials: getDefaultKindSelectState(),
  };
}

export function getDefaultLoggingInput(provider: string = `cloudWatch`): LoggingInput {
  return {
    id: uuidv4(),
    provider: provider as any,
    cloudWatch: getDefaultCloudWatchLoggingInput(),
    coralogix: getDefaultCoralogixLoggingInput(),
    datadog: getDefaultDatadogLoggingInput(),
    elastic: getDefaultElasticLoggingInput(),
    fluentd: getDefaultFluentdLoggingInput(),
    logzio: getDefaultLogzioLoggingInput(),
    opentelemetry: getDefaultOpenTelemetryLoggingInput(),
    s3: getDefaultS3LoggingInput(),
    stackdriver: getDefaultStackdriverLoggingInput(),
    syslog: getDefaultSyslogLoggingInput(),
  };
}

export function inputToCloudWatchLogging(input: CloudWatchLoggingInput): OrgLogConfig {
  const res: OrgLogConfig = {
    cloudWatch: {
      credentials: input.credentials.link,
      groupName: input.groupName,
      region: input.region,
      streamName: input.streamName,
    },
  };
  if (input.retentionDays) {
    res.cloudWatch!.retentionDays = Number(input.retentionDays);
  }
  const extractFields: Record<string, string> = {};
  for (const pair of input.extractFields) {
    extractFields[pair.name] = pair.value;
  }
  res.cloudWatch!.extractFields = extractFields;

  return res;
}

export function inputToCoralogixLogging(input: CoralogixLoggingInput): OrgLogConfig {
  const res: OrgLogConfig = {
    coralogix: {
      credentials: input.credentials.link,
      cluster: input.cluster,
    },
  };
  if (input.app) {
    res.coralogix!.app = input.app;
  }
  if (input.subsystem) {
    res.coralogix!.subsystem = input.subsystem;
  }

  return res;
}

export function inputToDatadogLogging(input: DatadogLoggingInput): OrgLogConfig {
  const res: OrgLogConfig = {
    datadog: {
      credentials: input.credentials.link,
      host: input.host,
    },
  };

  return res;
}

export function inputToElasticLoggingElasticAwsProvider(input: ElasticLoggingAwsProviderInput): OrgLogConfig {
  const res: OrgLogConfig = {
    elastic: {
      aws: {
        credentials: input.credentials.link,
        host: input.host,
        index: input.index,
        port: Number(input.port),
        region: input.region,
        type: input.type,
      },
    },
  };

  return res;
}

export function inputToElasticLoggingElastiCloudProvider(input: ElasticLoggingElastiCloudProviderInput): OrgLogConfig {
  const res: OrgLogConfig = {
    elastic: {
      elasticCloud: {
        cloudId: input.cloudId,
        credentials: input.credentials.link,
        index: input.index,
        type: input.type,
      },
    },
  };

  return res;
}

export function inputToElasticLoggingGenericProvider(input: ElasticLoggingGenericESProviderInput): OrgLogConfig {
  const res: OrgLogConfig = {
    elastic: {
      generic: {
        credentials: input.credentials.link,
        host: input.host,
        index: input.index,
        type: input.type,
      },
    },
  };

  if (input.path) {
    res.elastic!.generic!.path = input.path;
  }
  if (input.port) {
    res.elastic!.generic!.port = Number(input.port);
  }

  return res;
}

export function inputToElasticLogging(input: ElasticLoggingInput): OrgLogConfig {
  if (input.provider === "aws") {
    return inputToElasticLoggingElasticAwsProvider(input.aws);
  }

  if (input.provider === `elasticCloud`) {
    return inputToElasticLoggingElastiCloudProvider(input.elasticCloud);
  }

  return inputToElasticLoggingGenericProvider(input.generic);
}

export function inputToFluentdLogging(input: FluentdLoggingInput): OrgLogConfig {
  const res: OrgLogConfig = {
    fluentd: {
      host: input.host,
    },
  };

  if (input.port) {
    res.fluentd!.port = Number(input.port);
  }

  return res;
}

export function inputToLogzioLogging(input: LogzioLoggingInput): OrgLogConfig {
  const res: OrgLogConfig = {
    logzio: {
      credentials: input.credentials.link,
      listenerHost: input.listenerHost,
    },
  };

  return res;
}

export function inputToS3Logging(input: S3LoggingInput): OrgLogConfig {
  const res: OrgLogConfig = {
    s3: {
      bucket: input.bucket,
      credentials: input.credentials.link,
      region: input.region,
    },
  };
  if (input.prefix) {
    res.s3!.prefix = input.prefix;
  }

  return res;
}

export function inputToStackdriverLogging(input: StackdriverLoggingInput): OrgLogConfig {
  const res: OrgLogConfig = {
    stackdriver: {
      credentials: input.credentials.link,
      location: input.location,
    },
  };

  return res;
}

export function inputToSyslogLogging(input: SyslogLoggingInput): OrgLogConfig {
  const res: OrgLogConfig = {
    syslog: {
      format: input.format,
      host: input.host,
      mode: input.mode,
      port: Number(input.port),
      severity: Number(input.severity),
    },
  };

  return res;
}

export function inputToOpenTelemetryLogging(input: OpenTelemetryLoggingInput): OrgLogConfig {
  const headers: Record<string, string> = {};
  for (const pair of input.headers) {
    headers[pair.name] = pair.value;
  }

  const res: OrgLogConfig = {
    opentelemetry: {
      endpoint: input.endpoint,
      headers: headers,
    },
  };

  if (input.credentials.link) {
    res.opentelemetry!.credentials = input.credentials.link;
  }

  return res;
}

export function inputToLogging(input: LoggingInput): OrgLogConfig {
  if (input.provider === "cloudWatch") {
    return inputToCloudWatchLogging(input.cloudWatch);
  }

  if (input.provider === "coralogix") {
    return inputToCoralogixLogging(input.coralogix);
  }

  if (input.provider === "datadog") {
    return inputToDatadogLogging(input.datadog);
  }

  if (input.provider === `elastic`) {
    return inputToElasticLogging(input.elastic);
  }

  if (input.provider === `fluentd`) {
    return inputToFluentdLogging(input.fluentd);
  }

  if (input.provider === `logzio`) {
    return inputToLogzioLogging(input.logzio);
  }

  if (input.provider === `opentelemetry`) {
    return inputToOpenTelemetryLogging(input.opentelemetry);
  }

  if (input.provider === `s3`) {
    return inputToS3Logging(input.s3);
  }

  if (input.provider === `stackdriver`) {
    return inputToStackdriverLogging(input.stackdriver);
  }

  // assume syslog
  return inputToSyslogLogging(input.syslog);
}

export function cloudWatchLoggingToInput(log: CloudWatchLogging): LoggingInput {
  const { name, absolute } = parseLink(log.credentials);

  const res: LoggingInput = {
    ...getDefaultLoggingInput(),
    provider: "cloudWatch",
    cloudWatch: {
      ...getDefaultCloudWatchLoggingInput(),
      credentials: {
        value: name,
        link: absolute,
      },
      groupName: log.groupName,
      region: log.region,
      streamName: log.streamName,
    },
  };
  if (log.retentionDays) {
    res.cloudWatch!.retentionDays = String(log.retentionDays);
  }
  const extractFields: NameValuePair[] = [];
  for (const [key, value] of Object.entries(log.extractFields || {})) {
    extractFields.push({ id: uuidv4(), name: key, value: value });
  }
  res.cloudWatch!.extractFields = extractFields;

  return res;
}

export function coralogixLoggingToInput(log: CoralogixLogging): LoggingInput {
  const { name, absolute } = parseLink(log.credentials);

  const res: LoggingInput = {
    ...getDefaultLoggingInput(),
    provider: "coralogix",
    coralogix: {
      ...getDefaultCoralogixLoggingInput(),
      credentials: {
        value: name,
        link: absolute,
      },
      cluster: log.cluster,
    },
  };
  if (log.app) {
    res.coralogix!.app = log.app;
  }
  if (log.subsystem) {
    res.coralogix!.subsystem = log.subsystem;
  }

  return res;
}

export function datadogLoggingToInput(log: DatadogLogging): LoggingInput {
  const { name, absolute } = parseLink(log.credentials);

  const res: LoggingInput = {
    ...getDefaultLoggingInput(),
    provider: "datadog",
    datadog: {
      ...getDefaultDatadogLoggingInput(),
      credentials: {
        value: name,
        link: absolute,
      },
      host: log.host,
    },
  };

  return res;
}

export function elasticLoggingAWSProviderToInput(log: ElasticAwsProvider): LoggingInput {
  const { name, absolute } = parseLink(log.credentials);

  const res: LoggingInput = {
    ...getDefaultLoggingInput(),
    provider: "elastic",
    elastic: {
      ...getDefaultElasticLoggingInput(),
      provider: "aws",
      aws: {
        ...getDefaultElasticLoggingGenericProviderInput(),
        credentials: {
          value: name,
          link: absolute,
        },
        host: log.host,
        index: log.index,
        port: String(log.port),
        region: log.region,
        type: log.type,
      },
    },
  };

  return res;
}

export function elasticLoggingElastiCloudProviderToInput(log: ElastiCloudProvider): LoggingInput {
  const { name, absolute } = parseLink(log.credentials);

  const res: LoggingInput = {
    ...getDefaultLoggingInput(),
    provider: "elastic",
    elastic: {
      ...getDefaultElasticLoggingInput(),
      provider: "elasticCloud",
      elasticCloud: {
        ...getDefaultElasticLoggingElastiCloudProviderInput(),
        credentials: {
          value: name,
          link: absolute,
        },
        cloudId: log.cloudId,
        index: log.index,
        type: log.type,
      },
    },
  };

  return res;
}

export function elasticLoggingGenericProviderToInput(log: GenericESProvider): LoggingInput {
  const { name, absolute } = parseLink(log.credentials);

  const res: LoggingInput = {
    ...getDefaultLoggingInput(),
    provider: "elastic",
    elastic: {
      ...getDefaultElasticLoggingInput(),
      provider: "generic",
      generic: {
        ...getDefaultElasticLoggingGenericProviderInput(),
        credentials: {
          value: name,
          link: absolute,
        },
        host: log.host,
        index: log.index,
        type: log.type,
      },
    },
  };
  if (log.path) {
    res.elastic!.generic!.path = log.path;
  }
  if (log.port) {
    res.elastic!.generic!.port = String(log.port);
  }

  return res;
}

export function elasticLoggingToInput(log: ElasticLogging): LoggingInput {
  if (log.aws) {
    return elasticLoggingAWSProviderToInput(log.aws);
  }

  if (log.elasticCloud) {
    return elasticLoggingElastiCloudProviderToInput(log.elasticCloud);
  }
  // assume generic
  return elasticLoggingGenericProviderToInput(log.generic!);
}

export function fluentdLoggingToInput(log: FluentdLogging): LoggingInput {
  const res: LoggingInput = {
    ...getDefaultLoggingInput(),
    provider: "fluentd",
    fluentd: {
      ...getDefaultFluentdLoggingInput(),
      host: log.host,
    },
  };

  if (log.port) {
    res.fluentd!.port = String(log.port);
  }

  return res;
}

export function logzioLoggingToInput(log: LogzioLogging): LoggingInput {
  const { name, absolute } = parseLink(log.credentials);

  const res: LoggingInput = {
    ...getDefaultLoggingInput(),
    provider: "logzio",
    logzio: {
      ...getDefaultLogzioLoggingInput(),
      credentials: {
        value: name,
        link: absolute,
      },
      listenerHost: log.listenerHost,
    },
  };

  return res;
}

export function s3LoggingToInput(log: S3Logging): LoggingInput {
  const { name, absolute } = parseLink(log.credentials);

  const res: LoggingInput = {
    ...getDefaultLoggingInput(),
    provider: "s3",
    s3: {
      ...getDefaultS3LoggingInput(),
      credentials: {
        value: name,
        link: absolute,
      },
      bucket: log.bucket,
      region: log.region,
    },
  };

  if (log.prefix) {
    res.s3!.prefix = log.prefix;
  }

  return res;
}

export function stackdriverLoggingToInput(log: StackdriverLogging): LoggingInput {
  const { name, absolute } = parseLink(log.credentials);

  const res: LoggingInput = {
    ...getDefaultLoggingInput(),
    provider: "stackdriver",
    stackdriver: {
      ...getDefaultStackdriverLoggingInput(),
      credentials: {
        value: name,
        link: absolute,
      },
      location: log.location,
    },
  };

  return res;
}

export function syslogLoggingToInput(log: SyslogLogging): LoggingInput {
  const res: LoggingInput = {
    ...getDefaultLoggingInput(),
    provider: "syslog",
    syslog: {
      ...getDefaultSyslogLoggingInput(),
      format: log.format,
      host: log.host,
      mode: log.mode,
      port: String(log.port),
      severity: String(log.severity),
    },
  };

  return res;
}

export function openTelemetryLoggingToInput(log: OpenTelemetryLogging): LoggingInput {
  const headers: NameValuePair[] = [];
  for (const [key, value] of Object.entries(log.headers || {})) {
    headers.push({ id: uuidv4(), name: key, value: value });
  }

  const res: LoggingInput = {
    ...getDefaultLoggingInput(),
    provider: "opentelemetry",
    opentelemetry: {
      ...getDefaultOpenTelemetryLoggingInput(),
      endpoint: log.endpoint,
      headers: headers,
    },
  };

  if (log.credentials) {
    const { name, absolute } = parseLink(log.credentials);
    res.opentelemetry.credentials = {
      value: name,
      link: absolute,
    };
  }

  return res;
}

export function loggingToInput(log: OrgLogConfig): LoggingInput {
  if (log.cloudWatch) {
    return cloudWatchLoggingToInput(log.cloudWatch);
  }

  if (log.coralogix) {
    return coralogixLoggingToInput(log.coralogix);
  }

  if (log.datadog) {
    return datadogLoggingToInput(log.datadog);
  }

  if (log.elastic) {
    return elasticLoggingToInput(log.elastic);
  }

  if (log.fluentd) {
    return fluentdLoggingToInput(log.fluentd);
  }

  if (log.logzio) {
    return logzioLoggingToInput(log.logzio);
  }

  if (log.opentelemetry) {
    return openTelemetryLoggingToInput(log.opentelemetry);
  }

  if (log.s3) {
    return s3LoggingToInput(log.s3);
  }

  if (log.stackdriver) {
    return stackdriverLoggingToInput(log.stackdriver);
  }

  // assume syslog
  return syslogLoggingToInput(log.syslog!);
}

export function inputToOrgPatch(input: OrgInput): OrgPatch {
  const res: OrgPatch = {
    name: input.name,
    kind: "org",
    tags: inputToTags(input.tags),
    spec: {},
  };

  if (input.logging.length > 0) {
    res.spec.logging = inputToLogging(input.logging[0]);
  }
  if (input.logging.length > 1) {
    const extraLogging: OrgLogConfig[] = [];
    for (const log of input.logging.slice(1)) {
      extraLogging.push(inputToLogging(log));
    }
    if (extraLogging.length > 0) {
      res.spec.extraLogging = extraLogging;
    }
  }

  res.spec.tracing = inputToTracing(input.tracing);
  if (input.sessionTimeoutSeconds) {
    res.spec.sessionTimeoutSeconds = Number(input.sessionTimeoutSeconds);
  }
  if (input.authConfig.samlOnly || input.authConfig.domainAutoMembers === "self") {
    res.spec.authConfig = {
      samlOnly: input.authConfig.samlOnly,
      domainAutoMembers: input.authConfig.domainAutoMembers === "none" ? [] : [input.authConfig.samlDomain],
    };
  }
  if (
    input.observability.defaultAlertEmails.length > 0 ||
    input.observability.logsRetentionDays ||
    input.observability.tracesRetentionDays ||
    input.observability.metricsRetentionDays
  ) {
    res.spec.observability = {
      defaultAlertEmails: input.observability.defaultAlertEmails.map((i) => i.value),
      logsRetentionDays: Number(input.observability.logsRetentionDays),
      metricsRetentionDays: Number(input.observability.metricsRetentionDays),
      tracesRetentionDays: Number(input.observability.tracesRetentionDays),
    };
  }
  if (input.security.threatDetection.isEnabled) {
    res.spec.security = {
      threatDetection: {
        enabled: input.security.threatDetection.isEnabled,
      },
    };
    if (input.security.threatDetection.minimumSeverity) {
      res.spec.security!.threatDetection!.minimumSeverity = input.security.threatDetection.minimumSeverity;
    }
    if (input.security.threatDetection.syslog.host || input.security.threatDetection.syslog.port) {
      res.spec.security!.threatDetection!.syslog = {
        host: input.security.threatDetection.syslog.host,
        port: Number(input.security.threatDetection.syslog.port),
      };
      if (input.security.threatDetection.syslog.transport) {
        res.spec.security!.threatDetection!.syslog!.transport = input.security.threatDetection.syslog.transport;
      }
    }
  }
  if (input.description) {
    res.description = input.description;
  }
  return res;
}

export function orgToInput(org: Org, isSaml: boolean, samlDomain: string): OrgInput {
  const defaultInput = getDefaultOrgInput();
  const res: OrgInput = {
    ...defaultInput,
    id: org.id,
    version: org.version,
    name: org.name,
    description: org.description,
    tags: tagsToInput(org.tags),
  };
  for (const log of [org.spec?.logging, ...(org.spec?.extraLogging || [])]) {
    if (!log) {
      continue;
    }
    res.logging.push(loggingToInput(log));
  }

  if (res.logging.length > 0) {
    res.meta.activeKeyExternalLog = `external-logs-${res.logging[0].id}`;
  }

  res.tracing = tracingToInput(org.spec?.tracing);
  if (org.spec?.sessionTimeoutSeconds) {
    res.sessionTimeoutSeconds = String(org.spec.sessionTimeoutSeconds);
  }
  if (org.spec?.authConfig) {
    res.authConfig.isSaml = isSaml;
    res.authConfig.samlDomain =
      org.spec.authConfig.domainAutoMembers && org.spec.authConfig.domainAutoMembers.length > 0
        ? org.spec.authConfig.domainAutoMembers[0]
        : samlDomain;

    res.authConfig.samlOnly = org.spec.authConfig.samlOnly || false;
    res.authConfig.domainAutoMembers =
      org.spec.authConfig.domainAutoMembers && org.spec.authConfig.domainAutoMembers.length > 0 ? "self" : "none";
  }
  if (org.spec?.observability) {
    if (org.spec.observability.logsRetentionDays) {
      res.observability.logsRetentionDays = String(org.spec.observability.logsRetentionDays);
    }
    if (org.spec.observability.metricsRetentionDays) {
      res.observability.metricsRetentionDays = String(org.spec.observability.metricsRetentionDays);
    }
    if (org.spec.observability.tracesRetentionDays) {
      res.observability.tracesRetentionDays = String(org.spec.observability.tracesRetentionDays);
    }
    res.observability.defaultAlertEmails = (org.spec.observability.defaultAlertEmails || []).map((e) => ({
      id: uuidv4(),
      value: e,
    }));
  }
  if (org.spec?.security?.threatDetection) {
    res.security.threatDetection.isEnabled = org.spec.security.threatDetection.enabled || false;
    if (org.spec.security.threatDetection.minimumSeverity) {
      res.security.threatDetection.minimumSeverity = org.spec.security.threatDetection.minimumSeverity;
    }
    if (org.spec.security.threatDetection.syslog?.host) {
      res.security.threatDetection.syslog.host = org.spec.security.threatDetection.syslog.host;
    }
    if (org.spec.security.threatDetection.syslog?.port) {
      res.security.threatDetection.syslog.port = String(org.spec.security.threatDetection.syslog.port);
    }
    if (org.spec.security.threatDetection.syslog?.transport) {
      res.security.threatDetection.syslog.transport = org.spec.security.threatDetection.syslog.transport;
    }
  }
  return res;
}

export const loggingProviderOptions: { value: LoggingProvider; label: string }[] = [
  { value: "cloudWatch", label: "CloudWatch" },
  { value: "coralogix", label: "Coralogix" },
  { value: "datadog", label: "Datadog" },
  { value: "elastic", label: "Elastic" },
  { value: "fluentd", label: "Fluentd" },
  { value: "logzio", label: "Logz.io" },
  { value: "opentelemetry", label: "OpenTelemetry" },
  { value: "s3", label: "S3" },
  { value: "stackdriver", label: "Stackdriver" },
  { value: "syslog", label: "Syslog" },
];

export function getDefaultByLoggingProvider(provider: LoggingProvider) {
  switch (provider) {
    case "cloudWatch":
      return getDefaultCloudWatchLoggingInput();
    case "coralogix":
      return getDefaultCoralogixLoggingInput();
    case "datadog":
      return getDefaultDatadogLoggingInput();
    case "elastic":
      return getDefaultElasticLoggingInput();
    case "fluentd":
      return getDefaultFluentdLoggingInput();
    case "logzio":
      return getDefaultLogzioLoggingInput();
    case "opentelemetry":
      return getDefaultOpenTelemetryLoggingInput();
    case "s3":
      return getDefaultS3LoggingInput();
    case "stackdriver":
      return getDefaultStackdriverLoggingInput();
    case "syslog":
      return getDefaultSyslogLoggingInput();
  }
}

export function orgToPatch(_org: OrgPatch, current: Org, concurrency: OptimisticConcurrency) {
  const org: any = JSON.parse(JSON.stringify(_org));

  org[`$replace/spec`] = org.spec;
  delete org.spec;

  org[`$replace/tags`] = org.tags;
  delete org.tags;

  org.id = concurrency.id;
  org.version = concurrency.version;

  return org;
}
